import React, { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import BackButton from "@/components/BackButton";
import { List, CheckCircle2, Trash2, ArrowRight, Ban } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";

export default function Meals() {
  const { user } = useAuth();
  const [myBookings, setMyBookings] = useState([]);
  const [mealsBookingOpen, setMealsBookingOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Available dates for meals (competition days)
  const availableDates = [
    "2026-10-04", "2026-10-05", "2026-10-06", "2026-10-07", "2026-10-08", "2026-10-09", "2026-10-10"
  ];
  
  // Current selection state
  const [selectedMeals, setSelectedMeals] = useState({});
  
  const alreadyBookedMeals = React.useMemo(() => {
    const booked = new Set();
    myBookings.forEach(b => {
      b.meals.forEach(m => {
        booked.add(`${m.date}-${m.meal}`);
      });
    });
    return booked;
  }, [myBookings]);
  
  const navigate = useNavigate();

  const fetchBookings = async () => {
    try {
      const [{ data: bookings }, { data: meta }] = await Promise.all([
        api.get("/meals/me"),
        api.get("/meta/events"),
      ]);
      setMyBookings(bookings);
      setMealsBookingOpen(!!meta.meals_booking_open);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings();
  }, []);

  const toggleMeal = (date, mealType) => {
    setSelectedMeals(prev => {
      const current = prev[date] || [];
      if (current.includes(mealType)) {
        return { ...prev, [date]: current.filter(m => m !== mealType) };
      } else {
        return { ...prev, [date]: [...current, mealType] };
      }
    });
  };

  const isIndian = user?.nationality === "India";
  const currency = isIndian ? "INR" : "USD";
  const currencySymbol = isIndian ? "₹" : "$";
  const prices = isIndian 
    ? { "Breakfast": 150, "Lunch": 200, "Dinner": 200 }
    : { "Breakfast": 1.6, "Lunch": 2.61, "Dinner": 2.61 };

  const calculateTotal = () => {
    let total = 0;
    Object.keys(selectedMeals).forEach(date => {
      selectedMeals[date].forEach(meal => {
        total += prices[meal] || 0;
      });
    });
    // For USD, fix to 2 decimal places to prevent floating point errors
    return isIndian ? total : parseFloat(total.toFixed(2));
  };

  const handleCheckout = async () => {
    const total = calculateTotal();
    if (total === 0) {
      toast.error("Please select at least one meal.");
      return;
    }
    
    const payload = Object.keys(selectedMeals)
      .filter(date => selectedMeals[date].length > 0)
      .map(date => ({
        date,
        meals: selectedMeals[date]
      }));
      
    setSaving(true);
    try {
      const { data } = await api.post("/meals", payload);
      toast.success("Meal booking created! Redirecting to payment...");
      navigate(`/payment?meal_booking_id=${data.booking_id}`);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Failed to create meal booking");
    } finally {
      setSaving(false);
    }
  };

  const payBooking = async (booking_id) => {
    navigate(`/payment?meal_booking_id=${booking_id}`);
  };

  const deleteBooking = async (booking_id) => {
    if (!window.confirm("Cancel this unpaid booking?")) return;
    try {
      await api.delete(`/meals/${booking_id}`);
      toast.success("Booking cancelled");
      setMyBookings((p) => p.filter(b => b.booking_id !== booking_id));
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B1429]">
        <Navbar />
        <div className="pt-28 pb-20 max-w-7xl mx-auto px-6 lg:px-10 text-white text-center">Loading...</div>
      </div>
    );
  }



  const totalAmount = calculateTotal();

  return (
    <div className="min-h-screen bg-[#0B1429]">
      <Navbar />
      <div className="pt-28 pb-20 max-w-7xl mx-auto px-6 lg:px-10">
        <BackButton className="mb-6" />
        <div className="flex flex-col md:flex-row justify-between items-start gap-4 mb-6 text-white">
          <div>
            <div className="eyebrow mb-3">Dining</div>
            <h1 className="font-display font-black text-4xl md:text-5xl mb-2">Meal & Food <span className="text-[#00F0FF]">Booking</span></h1>
            <p className="text-[#94A3B8]">Pre-book your meals for the championship days.</p>
          </div>
        </div>

        {myBookings.length > 0 && (
          <div className="mb-10 text-white">
            <div className="eyebrow mb-3">Your Bookings</div>
            <div className="grid md:grid-cols-2 gap-4">
              {myBookings.map((b) => (
                <div key={b.booking_id} className="card-dark p-5 flex justify-between items-center">
                  <div>
                    <div className="font-semibold text-[#00F0FF] mb-1">Booking #{b.booking_id}</div>
                    <div className="text-xs text-[#94A3B8] mb-2 flex flex-col gap-0.5">
                      {b.meals.map((m, idx) => (
                         <span key={idx}>{m.date} - {m.meal} ({b.currency === "INR" ? "₹" : "$"}{m.price})</span>
                      ))}
                    </div>
                    <div className="text-sm font-bold">Total: {b.currency === "INR" ? "₹" : "$"}{b.amount_usd}</div>
                  </div>
                  <div className="flex flex-col gap-2 items-end">
                    {b.payment_status === "paid" ? (
                      <>
                        <span className="text-[#00E676] text-xs font-semibold flex items-center gap-1"><CheckCircle2 className="w-4 h-4" />PAID</span>
                        <a href={`/invoice/meals/${b.booking_id}`} className="text-[#00F0FF] hover:underline text-xs">Download Invoice →</a>
                      </>
                    ) : (
                      <div className="flex gap-2">
                        <button onClick={() => deleteBooking(b.booking_id)} className="btn-secondary !py-2 !px-3 text-xs text-[#FF3B30] hover:text-[#FF3B30] hover:border-[#FF3B30]/30" title="Cancel Booking"><Trash2 className="w-3.5 h-3.5" /></button>
                        <button onClick={() => payBooking(b.booking_id)} className="btn-primary !py-2 !px-4 text-xs">Pay Now</button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {!mealsBookingOpen ? (
          <div className="card-dark p-6 mb-10 text-white flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 bg-[#00F0FF]/10">
              <Ban className="w-5 h-5 text-[#00F0FF]" strokeWidth={1.5} />
            </div>
            <div>
              <h2 className="font-display font-bold text-lg mb-1">Meal Booking Closed</h2>
              <p className="text-sm text-[#94A3B8]">
                New meal bookings are closed until further notice. Existing bookings above can still be paid or cancelled.
              </p>
            </div>
          </div>
        ) : (
        <div className="card-dark p-6 mb-10 text-white">
          <h2 className="font-display font-bold text-xl mb-4">Book New Meals</h2>
          <div className="overflow-auto scroll-fade mb-6">
            <table className="w-full text-sm">
              <thead className="text-xs text-[#94A3B8] uppercase tracking-wider text-left">
                <tr>
                  <th className="pb-4 border-b border-[#2A3A5F]">Date</th>
                  <th className="pb-4 border-b border-[#2A3A5F]">Breakfast ({currencySymbol}{prices["Breakfast"]})</th>
                  <th className="pb-4 border-b border-[#2A3A5F]">Lunch ({currencySymbol}{prices["Lunch"]})</th>
                  <th className="pb-4 border-b border-[#2A3A5F]">Dinner ({currencySymbol}{prices["Dinner"]})</th>
                </tr>
              </thead>
              <tbody>
                {availableDates.map(date => {
                  const breakfastBooked = alreadyBookedMeals.has(`${date}-Breakfast`);
                  const lunchBooked = alreadyBookedMeals.has(`${date}-Lunch`);
                  const dinnerBooked = alreadyBookedMeals.has(`${date}-Dinner`);
                  
                  const breakfastSelected = selectedMeals[date]?.includes("Breakfast");
                  const lunchSelected = selectedMeals[date]?.includes("Lunch");
                  const dinnerSelected = selectedMeals[date]?.includes("Dinner");
                  
                  return (
                    <tr key={date} className="border-b border-[#2A3A5F] hover:bg-white/[0.01]">
                      <td className="py-4 font-mono text-[#CBD5E1]">{date}</td>
                      <td className="py-4">
                        <label className={`flex items-center gap-2 p-3 -m-3 ${breakfastBooked ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                          <input type="checkbox" checked={breakfastBooked || breakfastSelected || false} disabled={breakfastBooked} onChange={() => toggleMeal(date, "Breakfast")} className={`w-4 h-4 accent-[#00F0FF] ${breakfastBooked ? 'cursor-not-allowed' : 'cursor-pointer'}`} />
                          {breakfastBooked && <span className="text-xs text-[#94A3B8] italic">Booked</span>}
                        </label>
                      </td>
                      <td className="py-4">
                        <label className={`flex items-center gap-2 p-3 -m-3 ${lunchBooked ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                          <input type="checkbox" checked={lunchBooked || lunchSelected || false} disabled={lunchBooked} onChange={() => toggleMeal(date, "Lunch")} className={`w-4 h-4 accent-[#00F0FF] ${lunchBooked ? 'cursor-not-allowed' : 'cursor-pointer'}`} />
                          {lunchBooked && <span className="text-xs text-[#94A3B8] italic">Booked</span>}
                        </label>
                      </td>
                      <td className="py-4">
                        <label className={`flex items-center gap-2 p-3 -m-3 ${dinnerBooked ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                          <input type="checkbox" checked={dinnerBooked || dinnerSelected || false} disabled={dinnerBooked} onChange={() => toggleMeal(date, "Dinner")} className={`w-4 h-4 accent-[#00F0FF] ${dinnerBooked ? 'cursor-not-allowed' : 'cursor-pointer'}`} />
                          {dinnerBooked && <span className="text-xs text-[#94A3B8] italic">Booked</span>}
                        </label>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t border-[#2A3A5F]">
            <div className="text-lg">
              <span className="text-[#94A3B8]">Total Amount:</span> <span className="font-bold text-[#00F0FF]">{currencySymbol}{totalAmount}</span>
            </div>
            <button
              onClick={handleCheckout}
              disabled={saving || totalAmount === 0}
              className="btn-primary disabled:opacity-50 flex items-center gap-2"
            >
              {saving ? "Processing..." : "Checkout"} <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
        )}

      </div>
    </div>
  );
}
